import React, { FC } from 'react'

import moment from 'moment/min/moment-with-locales'
import { twMerge } from 'tailwind-merge'

import { Url, MediaProps, SizeProps } from '@/types'
import { Badge } from '@components/Badge'
import { Card } from '@components/Card'
import { type CardOverlayProps } from '@components/Card/CardOverlay'
import { type ImageProps } from '@components/Image'
import { formatDate } from '@helpers/date'
import { getLocale } from '@helpers/translator'

// Initialiser moment avec la locale actuelle
moment.locale(getLocale())

/**
 * Interface pour les propriétés du composant MediaCard
 */
export interface MediaCardProps extends MediaProps, SizeProps {
  id: number
  title?: string
  image?: ImageProps
  metadata?: string
  overlay: CardOverlayProps
  url?: Url
  startDate?: string
  endDate?: string
  content_type?: string
  type?: string[] // Accepter les types directement depuis les données d'API
}

/**
 * Composant MediaCard - Affiche une carte média avec image, titre et métadonnées
 */
export const MediaCard: FC<MediaCardProps> = ({
  aspectRatio,
  size = 'md',
  id,
  title,
  image,
  metadata,
  backgroundColor,
  displayFlag = false,
  displayFavorite = false,
  overlay,
  url,
  titleClassName,
  className,
  metadataClassName,
  tags,
  type, // Recevoir aussi la propriété type directement
  startDate,
  endDate,
  content_type,
}) => {
  // Déterminer la source des tags à utiliser (utiliser type s'il est disponible et que tags ne l'est pas)
  const effectiveTags = tags || type || []

  // Formater la date d'événement si disponible, utiliser metadata si fourni ou générer à partir de startDate/endDate
  let formattedDate = metadata

  // Si nous n'avons pas de métadonnées mais des dates, les formater
  if (!formattedDate && (startDate || endDate)) {
    formattedDate = formatDate(startDate, endDate)
  }

  return (
    <Card.Wrapper className={`gap-y-4 flex flex-col flex-1`}>
      <Card.Content
        title={title}
        url={url}
        aspectRatio={aspectRatio}
        id={id}
        image={image}
        displayFlag={displayFlag}
        displayFavorite={displayFavorite}
        backgroundColor={backgroundColor}
        className={`flex-1 ${className}`}
        size={size}
      >
        {/* Afficher les badges dans la même disposition que POICard */}
        {effectiveTags &&
          Array.isArray(effectiveTags) &&
          effectiveTags.length > 0 && (
            <div className="flex items-center gap-2 p-5">
              {effectiveTags.map((tag, index) => {
                return (
                  tag && (
                    <Badge
                      className="relative flex"
                      key={`${tag}-${index}`}
                      data-testid={`tag-${index}`}
                    >
                      {tag}
                    </Badge>
                  )
                )
              })}
            </div>
          )}

        <Card.Overlay
          overlayHeight={overlay?.overlayHeight}
          overlayWidth={overlay?.overlayWidth}
          position={overlay?.position}
          size={size}
          className={'lg:pb-7'}
        >
          <Card.Title
            className={twMerge(
              'text-white text-[22px] leading-[23px] font-[400] tracking-[2%] line-clamp-2 text-balance',
              titleClassName,
            )}
          >
            {title}
          </Card.Title>

          {/* Afficher la date formatée en gris clair (grey-soft) */}
          {formattedDate && (
            <Card.Metadata
              className={twMerge(
                'text-grey-soft text-base font-[450] mt-2',
                metadataClassName,
              )}
            >
              {formattedDate}
            </Card.Metadata>
          )}
        </Card.Overlay>
      </Card.Content>
    </Card.Wrapper>
  )
}

export default MediaCard
